import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import config from '../../config';
import { useGlobalContext } from '../../GlobalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetByLevelId } from '../../service/UserLevelService';
import { GetLatestMemberByRole } from '../../service/UserService';
import { GetLatestEpinPurchases } from '../../service/EpinPurchaseService';
import { GetLatestBlogs } from '../../service/BlogService';


function Dashboard() {
  const {
    isLoading, setIsLoading,
    setAppError, setAppErrorTitle, setAppErrorMessage, setAppErrorMode
  } = useGlobalContext();

  //user level counts
  const [levelCounts, setLevelCounts] = useState({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setIsLoading(true);
        const updatedCounts = {};

        for (let levelId = 1; levelId <= 5; levelId++) {
          const res = await GetByLevelId(levelId, 1, 1, ""); // Only need count

          if (res && typeof res === "object" && "status" in res && res.status === 200) {
            updatedCounts[levelId] = res.total || 0;
          } else {
            updatedCounts[levelId] = 0;
          }
        }

        setLevelCounts(updatedCounts);
      } catch (err) {
        setAppError(true);
        setAppErrorTitle("Error loading member counts");
        setAppErrorMessage(err?.message || "Unexpected error occurred");
        setAppErrorMode("danger");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounts();
  }, []);


  //user latest lists
  const [users, setUsers] = useState([]);
  const fetchUserList = async () => {
    try {
      setIsLoading(true);
      const response = await GetLatestMemberByRole("member", 10); // high limit, page = 1
      setUsers(response.users || []);
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load data");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, []);

  //epin purchase latest
  const [epinPurchases, setEpinPurchases] = useState([]);
  const [overallTotal, setOverallTotal] = useState(0); // ← new state

  const fetchEpinPurchaseList = async () => {
    try {
      setIsLoading(true);
      const response = await GetLatestEpinPurchases(5);
      setEpinPurchases(response.epinPurchases || []);
      setOverallTotal(response.overallTotal || 0); // ← store the overall total
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load data");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchEpinPurchaseList();
  }, []);


  //latest blogs
  const [blogs, setBlogs] = useState([]);
  const fetchLatestBlogs = async () => {
    try {
      setIsLoading(true);
      const response = await GetLatestBlogs(4); // Load latest 4 blogs
      setBlogs(response.blogs || []);
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load latest blogs");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchLatestBlogs();
  }, []);



  return (
    <>
      <Helmet>
        <title>Dashboard | {config.appName}</title>
      </Helmet>

      <div className='container'>

        {/* start of page */}
        <div className='page'>

          <div className='page-heading'>
            <h1>Dashboard</h1>
          </div>

          {/* start of page content */}
          <div className='page-content'>

            {/* start of dashboard counter section */}
            <div className='dashboard-counter-section'>
              <div className='row'>
                <div className='col-12 col-sm-6 col-md-4 col-lg-3'>
                  <div className='dashboard-counter'>
                    <i className="bi bi-people"></i>
                    <span className='counter-value'>{levelCounts[1]}</span>
                    <b>Level 1</b>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col-md-4 col-lg-2'>
                  <div className='dashboard-counter dashboard-counter-green'>
                    <i className="bi bi-people"></i>
                    <span className='counter-value'>{levelCounts[2]}</span>
                    <b>Level 2</b>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col-md-4 col-lg-3'>
                  <div className='dashboard-counter dashboard-counter-red'>
                    <i className="bi bi-people"></i>
                    <span className='counter-value'>{levelCounts[3]}</span>
                    <b>Level 3</b>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col-md-4 col-lg-2'>
                  <div className='dashboard-counter dashboard-counter-blue'>
                    <i className="bi bi-people"></i>
                    <span className='counter-value'>{levelCounts[4]}</span>
                    <b>Level 4</b>
                  </div>
                </div>
                <div className='col-12 col-sm-6 col-md-4 col-lg-2'>
                  <div className='dashboard-counter dashboard-counter-green'>
                    <i className="bi bi-people"></i>
                    <span className='counter-value'>{levelCounts[5]}</span>
                    <b>Level 5</b>
                  </div>
                </div>
              </div>
            </div>

            {/* end of dashboard counter section */}

            {/* lastest members */}
            <div className='row'>
              <div className='col-lg-12 col-md-8 col-sm-12 col-12'>
                {/* start of portal content */}
                <div className="portal">
                  {/* start of portal title */}
                  <div className='portal-title-bar'>
                    <h4 className='portal-heading'>Latest Members</h4>
                    <ul className='portal-actions d-none'>
                      <li>
                        <Link to='#'>
                          <i className="bi bi-arrow-clockwise"></i>
                        </Link>
                      </li>
                    </ul>
                  </div>
                  {/* end of portal title */}

                  {/* start of portal body */}
                  <div className='portal-body'>
                    <div className='table-content'>
                      <div className="mobile-scroll">
                        <table className='table table-bordered table-condensed'>
                          <thead>
                            <tr>
                              <th>Member Name</th>
                              <th>Gender</th>
                              <th>Mobile</th>
                              <th>Email</th>
                              <th>Whatsapp</th>
                              <th>City</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users && users.length > 0 ? (
                              users.map((user) => (
                                <tr key={user.id}>
                                  <td>{user.name}</td>
                                  <td>{user.gender?.name || '-'}</td>
                                  <td>{user.mobile}</td>
                                  <td>{user.email}</td>
                                  <td>{user.whatsapp}</td>
                                  <td>{user.city?.name || '-'}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="9" className="text-center">No members found</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  {/* end of portal title */}

                </div>
                {/* end of portal content */}
              </div>
            </div>

            <br></br>

            {/* lastest epin purchase */}
            <div className='row'>
              <div className='col-lg-12 col-md-4 col-sm-12 col-12'>
                {/* start of portal content */}
                <div className="portal">
                  {/* start of portal title */}
                  <div className='portal-title-bar'>
                    <h4 className='portal-heading'>Latest Epin Purchase</h4>
                    <ul className='portal-actions d-none'>
                      <li>
                        <Link to='#'>
                          <i className="bi bi-arrow-clockwise"></i>
                        </Link>
                      </li>
                    </ul>
                  </div>
                  {/* end of portal title */}

                  {/* start of portal body */}
                  <div className='portal-body'>
                    <div className='table-content'>
                      <div className="mobile-scroll">
                        <table className='table table-bordered table-condensed'>
                          <thead>
                            <tr>
                              <th>Member Name</th>
                              <th>Level</th>
                              <th>Purchase Date</th>
                              <th>Price</th>
                              <th>Unit</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {epinPurchases && epinPurchases.length > 0 ? (
                              epinPurchases.map((epinPurchase) => (
                                <tr key={epinPurchase.id}>
                                  <td>{epinPurchase.user.name || '-'}</td>
                                  <td>{epinPurchase.level.name || '-'}</td>
                                  <td>
                                    {epinPurchase.purchaseDate?.date && (() => {
                                      const date = new Date(epinPurchase.purchaseDate.date);
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const year = date.getFullYear();
                                      return `${day}-${month}-${year}`;
                                    })()}
                                  </td>
                                  <td>{epinPurchase.price}</td>
                                  <td>{epinPurchase.unit}</td>
                                  <td>{epinPurchase.total}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="9" className="text-center">No epin purchase found</td></tr>
                            )}
                            {/* Overall Total Row */}
                            {epinPurchases.length > 0 && (
                              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                                <td colSpan="5" className="text-end">Overall Total</td>
                                <td>{overallTotal}</td>
                              </tr>
                            )}
                          </tbody>

                        </table>
                      </div>
                    </div>
                  </div>
                  {/* end of portal title */}

                </div>
                {/* end of portal content */}
              </div>
            </div>

            <br></br>

            {/* lastest blog */}
            <div className='row'>
              <div className='col-lg-12 col-md-4 col-sm-12 col-12'>
                {/* start of portal content */}
                <div className="portal">
                  {/* start of portal title */}
                  <div className='portal-title-bar'>
                    <h4 className='portal-heading'>Latest Blogs</h4>
                    <ul className='portal-actions d-none'>
                      <li>
                        <Link to='#'>
                          <i className="bi bi-arrow-clockwise"></i>
                        </Link>
                      </li>
                    </ul>
                  </div>
                  {/* end of portal title */}

                  {/* start of portal body */}
                  <div className='portal-body latest-blog'>
                    <div className='table-content'>
                      <div className="row g-4">
                        {blogs && blogs.length > 0 ? (
                          blogs.map((blog) => (
                            <div className="col-md-6 col-lg-4 col-xl-3" key={blog.id}>
                              <div className="card shadow-sm border-0 rounded-4 overflow-hidden position-relative">
                                {/* Top colored image placeholder with date badge */}
                                <div className="align-items-start justify-content-start position-relative" style={{ height: '180px' }}>
                                  <span className="badge bg-dark position-absolute top-0 start-0 m-2">
                                    {(() => {
                                      const date = new Date(blog.createdDateTime);
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
                                      return `${day} ${month}`;
                                    })()}
                                  </span>
                                  <div className="justify-content-center align-items-center text-white fs-4 fw-bold">
                                    <Link to={`/blog/view/${blog.id}`}>
                                      <img
                                        src={`${config.imageBasePath}/blog/${blog.id}.${blog.image}`}
                                        alt={blog.name}
                                        className='img-fluid image-xs latest-blog-img'
                                      />
                                    </Link>
                                  </div>
                                </div>

                                {/* Blog content */}
                                <div className="card-body">
                                  <p className="text-secondary small mb-1 mt-1"><i className="bi bi-list me-1" ></i>{blog.blogCategory.name}</p>
                                  <Link to={`/blog/view/${blog.id}`} className="text-decoration-none text-dark w-bold">
                                    {blog.name?.substring(0, 20)}
                                  </Link>
                                  <p className="card-text text-muted">{blog.description?.substring(0, 80) + '...'}</p>
                                  <hr></hr>
                                  <p className="text-secondary small mb-0">
                                    <i className="bi bi-calendar-event"></i>{" "}
                                    {(() => {
                                      const date = new Date(blog.createdDateTime);
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const year = date.getFullYear();
                                      return `${day}-${month}-${year}`;
                                    })()}
                                    <i className="bi bi-clock ms-2 me-1"></i>
                                    {(() => {
                                      const date = new Date(blog.createdDateTime);
                                      let hours = date.getHours();
                                      const minutes = String(date.getMinutes()).padStart(2, '0');
                                      const ampm = hours >= 12 ? 'PM' : 'AM';
                                      hours = hours % 12;
                                      hours = hours ? hours : 12;
                                      return `${hours}:${minutes} ${ampm}`;
                                    })()}
                                  </p>
                                </div>
                              </div>
                            </div>

                          ))
                        ) : (
                          <div className="col-12 text-center">
                            <p>No blogs found</p>
                          </div>
                        )}
                      </div>

                      {/* View More Button */}
                      <div className="text-center mt-4">
                        <Link to="blog/list/grid" className="btn btn-primary px-4 rounded-pill">
                          View More
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* end of portal title */}

                </div>
                {/* end of portal content */}
              </div>

            </div>
          </div>
          {/* end of page content */}

        </div>
        {/* end of page */}

      </div>
    </>
  )
}

export default Dashboard